import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { authSchema } from "@/lib/validation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Receipt, MessageSquare, PieChart } from "lucide-react";

interface GuestSignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GuestSignupModal({ open, onOpenChange }: GuestSignupModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSignUp = async () => {
    try {
      const result = authSchema.safeParse({ email, password });
      if (!result.success) {
        const errorMessages = result.error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessages,
          variant: "destructive",
        });
        return;
      }

      setLoading(true);
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) {
        if (error.message.includes("already registered")) {
          toast({
            title: "Account exists",
            description: "This email is already registered. Please sign in instead.",
            variant: "destructive",
          });
        } else {
          throw error;
        }
        return;
      }

      toast({
        title: "Welcome aboard! 🎉",
        description: "Your account is ready. Let's save you some money!",
      });
      
      // Clear guest trial on successful signup
      localStorage.removeItem('guest_trial_start');
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "Welcome back! 👋",
        description: "Let's continue saving you money!",
      });
      
      // Clear guest trial on successful login
      localStorage.removeItem('guest_trial_start');
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md mx-4">
        <DialogHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <div className="p-3 bg-primary/10 rounded-full">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-2xl">You're loving it! 🎉</DialogTitle>
          <DialogDescription className="text-base pt-2">
            Create a free account to save your chats and unlock all features
          </DialogDescription>
        </DialogHeader>

        {/* Benefits */}
        <div className="grid grid-cols-3 gap-3 py-4">
          <div className="flex flex-col items-center text-center p-3 bg-muted/50 rounded-lg">
            <MessageSquare className="w-5 h-5 text-primary mb-1" />
            <span className="text-xs font-medium">Save Chats</span>
          </div>
          <div className="flex flex-col items-center text-center p-3 bg-muted/50 rounded-lg">
            <Receipt className="w-5 h-5 text-primary mb-1" />
            <span className="text-xs font-medium">Track Receipts</span>
          </div>
          <div className="flex flex-col items-center text-center p-3 bg-muted/50 rounded-lg">
            <PieChart className="w-5 h-5 text-primary mb-1" />
            <span className="text-xs font-medium">Get Summaries</span>
          </div>
        </div>

        <Tabs defaultValue="signup" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
            <TabsTrigger value="login">Log In</TabsTrigger>
          </TabsList>

          <TabsContent value="signup" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
            </div>
            <Button
              onClick={handleSignUp}
              disabled={loading}
              className="w-full h-12 text-base"
            >
              {loading ? "Creating account..." : "Create Free Account"}
            </Button>
          </TabsContent>

          <TabsContent value="login" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
            </div>
            <Button
              onClick={handleSignIn}
              disabled={loading}
              className="w-full h-12 text-base"
            >
              {loading ? "Logging in..." : "Log In"}
            </Button>
          </TabsContent>
        </Tabs>

        <p className="text-xs text-center text-muted-foreground pt-2">
          Free 7-day trial • No credit card required
        </p>
      </DialogContent>
    </Dialog>
  );
}
